import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { CategoriesPageView } from './CategoriesPageView';
import { useCategoriesPage } from './useCategoriesPage';

/**
 * `/accounting/operations/categories` — the taxonomy every operation is filed
 * under, and the screen that owns it.
 *
 * Every org is seeded the same six and may then rename, add and remove them,
 * because a taxonomy describes how *this* business thinks about its money and no
 * fixed list survives contact with the second tenant. Until this screen existed
 * the set was seeded and read-only in practice: the field shipped with the chip,
 * and the editing was named as its own change.
 *
 * A standalone sub-page outside the tab shell, like the editor: it configures
 * the *catalog*, not one operation, and a fourth tab
 * would give a six-row list the same weight as the catalog itself. The navbar's
 * back button comes from `SUBPAGE_NAVIGATION`; the screen carries its own back
 * link too, because it is reached from a toolbar button rather than from a row.
 *
 * Owns its data hook and an `ErrorBoundary`; the throw lives in the inner
 * Content child, because React boundaries do not catch errors thrown by the
 * component that renders them. A failed **read** throws: a list that will not
 * load and a tenant with one category look alike on screen, and every control
 * here acts on the list being right. A refused **write** does not — it is
 * expected, actionable, and rendered beside the control that caused it.
 */
export const CategoriesPage: React.FC = () => {
    const page = useCategoriesPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('operations.categories.error.title'),
            errorMessage: t('operations.categories.error.message'),
            errorRetry: t('operations.categories.error.retry'),
        }),
        [t],
    );

    return (
        <div
            className="flex w-full flex-col gap-6"
            {...getNodeTestIdProp('operation-categories-page')}
        >
            <ErrorBoundary
                data-testid="operation-categories-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="operation-categories-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <CategoriesPageContent page={page} />
            </ErrorBoundary>
        </div>
    );
};

type CategoriesPageContentProps = {
    page: ReturnType<typeof useCategoriesPage>;
};

const CategoriesPageContent: React.FC<CategoriesPageContentProps> = ({
    page,
}) => {
    if (page.error) {
        throw page.error;
    }

    return (
        <CategoriesPageView
            access={page.access}
            blockingCount={page.blockingCount}
            canRead={page.canRead}
            deleteError={page.deleteError}
            deleteTarget={page.deleteTarget}
            form={page.form}
            formError={page.formError}
            isDeleting={page.isDeleting}
            isFormSubmittable={page.isFormSubmittable}
            isLoading={page.isLoading}
            isSaving={page.isSaving}
            locale={page.locale}
            onAdd={page.canEdit ? page.onAdd : null}
            onBack={page.onBack}
            onDelete={page.canEdit ? page.onDelete : null}
            onDeleteCancel={page.onDeleteCancel}
            onDeleteConfirm={page.onDeleteConfirm}
            onFormCancel={page.onFormCancel}
            onFormNameEnChange={page.onFormNameEnChange}
            onFormNameKaChange={page.onFormNameKaChange}
            onFormSubmit={page.onFormSubmit}
            onRename={page.canEdit ? page.onRename : null}
            rows={page.rows}
        />
    );
};
